// Capacitor 네이티브(Android) 래퍼 전용 동작.
// index.js에서 한 번만 import/실행한다. Capacitor.isNativePlatform()이 false인
// 일반 웹 브라우저에서는 initNativeApp()이 즉시 리턴하는 완전한 no-op이다.
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';

const BACK_EXIT_WINDOW_MS = 2000;
const EXIT_TOAST_ID = 'kmes-exit-toast';

let lastBackPressAt = null;

// ---- 순수 로직 (유닛 테스트 대상) ----

// 열린 MUI Dialog/Drawer(=Modal 포털 기반 오버레이)를 찾기 위한 선택자.
// variant="persistent"/"permanent" Drawer(예: 좌측 메뉴)는 Modal 포털을 쓰지
// 않고 늘 DOM에 남아있으므로 이 선택자에 걸리지 않는다 — 즉 "닫을 수 있는"
// Dialog/Drawer/Menu/Popover만 감지한다.
export const OPEN_OVERLAY_SELECTOR = '.MuiModal-root';

export function hasOpenOverlay(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return false;
  return !!doc.querySelector(OPEN_OVERLAY_SELECTOR);
}

// 뒤로가기를 눌렀을 때 취할 행동을 결정하는 순수 함수.
//   1) 열린 오버레이가 있으면 닫는다
//   2) 아니고 SPA 히스토리를 더 거슬러 갈 수 있으면 뒤로간다
//   3) 아니면(메인 화면) 종료 여부를 묻는다
export function decideBackAction({ hasOverlay, canGoBack }) {
  if (hasOverlay) return 'closeOverlay';
  if (canGoBack) return 'goBack';
  return 'exitPrompt';
}

// 메인 화면에서 "한 번 더 누르면 종료" 판정 (순수 함수).
export function shouldExitOnSecondPress(lastPressAt, now = Date.now(), windowMs = BACK_EXIT_WINDOW_MS) {
  return typeof lastPressAt === 'number' && now - lastPressAt < windowMs;
}

// ---- DOM 부수효과 (네이티브에서만 호출됨) ----

function dispatchEscape() {
  if (typeof document === 'undefined') return;
  const event = new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    keyCode: 27,
    which: 27,
    bubbles: true,
    cancelable: true,
  });
  document.dispatchEvent(event);
}

function showExitToast() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(EXIT_TOAST_ID)) return;

  const toast = document.createElement('div');
  toast.id = EXIT_TOAST_ID;
  toast.textContent = '한 번 더 누르면 앱이 종료됩니다';
  Object.assign(toast.style, {
    position: 'fixed',
    left: '50%',
    bottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
    transform: 'translateX(-50%)',
    background: 'rgba(33, 33, 33, 0.9)',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '20px',
    fontSize: '14px',
    zIndex: 2147483647,
    pointerEvents: 'none',
    transition: 'opacity 0.2s ease',
  });
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 200);
  }, BACK_EXIT_WINDOW_MS - 200);
}

async function setupStatusBar() {
  try {
    // overlay: true 여야 한다. 상태바 높이만큼의 여백은 이미 capacitor.config 의
    // adjustMarginsForEdgeToEdge: "force" 가 WebView 바깥 여백으로 넣어 준다. 여기서 overlay: false 로 두면
    // 같은 높이의 여백이 한 번 더 들어가 상태바와 상단 헤더 사이에 빈 줄이 생긴다(2026-10-15 실기기·에뮬레이터 확인).
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setBackgroundColor({ color: '#37474f' });
    await StatusBar.setStyle({ style: Style.Dark }); // Style.Dark = 밝은(흰색) 아이콘, 어두운 배경용
  } catch (e) {
    // 일부 OEM/권한 환경에서 실패할 수 있음 — 치명적이지 않으므로 무시
    // eslint-disable-next-line no-console
    console.warn('[nativeApp] status bar setup failed', e);
  }
}

// 앱 안에서 뒤로 갈 화면이 있는지. 안드로이드가 주는 canGoBack 은 SPA(pushState) 이동을 못 세는
// 경우가 있어(2026-10-15 에뮬레이터: 여러 화면을 이동했는데도 false) React Router 가 history.state 에
// 기록하는 idx(이 탭에서 몇 번째 화면인지)를 함께 본다.
export function spaCanGoBack(win = typeof window !== 'undefined' ? window : null) {
  const idx = win && win.history && win.history.state && win.history.state.idx;
  return typeof idx === 'number' && idx > 0;
}

function handleBackButton({ canGoBack }) {
  const overlay = hasOpenOverlay();
  const action = decideBackAction({ hasOverlay: overlay, canGoBack: canGoBack || spaCanGoBack() });

  if (action === 'closeOverlay') {
    dispatchEscape();
    return;
  }

  if (action === 'goBack') {
    window.history.back();
    return;
  }

  // action === 'exitPrompt' (메인 화면)
  const now = Date.now();
  if (shouldExitOnSecondPress(lastBackPressAt, now)) {
    App.exitApp();
    return;
  }
  lastBackPressAt = now;
  showExitToast();
}

// index.js에서 1회만 호출한다.
export function initNativeApp() {
  if (!Capacitor.isNativePlatform()) return; // 웹: 완전한 no-op

  setupStatusBar();
  App.addListener('backButton', handleBackButton);
}

export default initNativeApp;
